
import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { ChatGroup } from '@/types/chat';
import { ChatCategory, chatCategories } from '@/types/chatCategories';
import { Users, MapPin, MessageSquare, Briefcase, HelpCircle, Shield } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface ChatGroupListProps {
  chatGroups: ChatGroup[];
  selectedGroup: ChatGroup | null;
  onSelectGroup: (group: ChatGroup) => Promise<void>;
  selectedCategory?: ChatCategory | null;
}

interface GroupMemberCounts {
  [groupId: string]: number;
}

const ChatGroupList = ({ chatGroups, selectedGroup, onSelectGroup, selectedCategory }: ChatGroupListProps) => {
  const [memberCounts, setMemberCounts] = useState<GroupMemberCounts>({});

  // Load member counts for all groups
  useEffect(() => {
    const loadMemberCounts = async () => {
      if (chatGroups.length === 0) return;

      try {
        console.log('Loading member counts for', chatGroups.length, 'groups');
        
        const counts: GroupMemberCounts = {};
        
        // Get member counts for all groups in parallel
        await Promise.all(
          chatGroups.map(async (group) => {
            const { data: members, error } = await supabase
              .from('chat_group_users')
              .select('user_id')
              .eq('chat_group_id', group.chat_id)
              .eq('is_active', true);

            if (!error && members) {
              counts[group.chat_id] = members.length;
              console.log(`Group ${group.chat_name}: ${members.length} members`);
            } else {
              counts[group.chat_id] = 0;
              console.error(`Error loading members for ${group.chat_name}:`, error);
            }
          })
        );

        setMemberCounts(counts);
        console.log('✅ Loaded member counts:', counts);
      } catch (error) {
        console.error('❌ Error loading member counts:', error);
      }
    };

    loadMemberCounts();
  }, [chatGroups]);
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'users': return <Users className="h-3 w-3" />;
      case 'map-pin': return <MapPin className="h-3 w-3" />;
      case 'message-square': return <MessageSquare className="h-3 w-3" />;
      case 'briefcase': return <Briefcase className="h-3 w-3" />;
      case 'help-circle': return <HelpCircle className="h-3 w-3" />;
      case 'shield': return <Shield className="h-3 w-3" />;
      default: return <MessageSquare className="h-3 w-3" />;
    }
  };

  const getCategoryFromGroupName = (groupName: string): ChatCategory | null => {
    const match = groupName.match(/^\[(.+?)\]/);
    if (!match) return null;
    
    const categoryName = match[1].toLowerCase().replace(' ', '-');
    return chatCategories.find(cat => 
      cat.name.toLowerCase() === match[1].toLowerCase()
    )?.id || null;
  };

  const getDisplayName = (groupName: string) => {
    return groupName.replace(/^\[.+?\]\s*/, '');
  };

  if (chatGroups.length === 0) {
    return (
      <div className="p-4 text-center text-gray-400">
        {selectedCategory 
          ? `No ${selectedCategory} groups found`
          : 'No chat groups yet'
        }
      </div>
    );
  }

  return (
    <div className="p-2 space-y-1">
      {chatGroups.map((group) => {
        const category = getCategoryFromGroupName(group.chat_name);
        const categoryInfo = category ? chatCategories.find(cat => cat.id === category) : null;
        const displayName = getDisplayName(group.chat_name);
        
        return (
          <button
            key={group.chat_id}
            onClick={async () => {
              console.log('=== SELECTING GROUP FROM LIST ===');
              console.log('Group:', group.chat_name, 'ID:', group.chat_id);
              await onSelectGroup(group);
            }}
            className={`w-full text-left p-3 rounded-lg transition-colors ${
              selectedGroup?.chat_id === group.chat_id
                ? 'bg-sports-green text-black'
                : 'hover:bg-gray-700 text-white'
            }`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="font-medium truncate flex-1">{displayName}</span>
              {categoryInfo && (
                <Badge 
                  variant="outline" 
                  className={`ml-2 text-xs ${categoryInfo.color} border-none text-white`}
                >
                  {getIcon(categoryInfo.icon)}
                  <span className="ml-1">{categoryInfo.name}</span>
                </Badge>
              )}
            </div>
            <div className="text-xs opacity-75 flex items-center justify-between">
              <span>{group.created_at && new Date(group.created_at).toLocaleDateString()}</span>
              <span className="flex items-center">
                <Users className="h-3 w-3 mr-1" />
                {memberCounts[group.chat_id] || 0} members
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default ChatGroupList;
